import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/app/store";
import { RootState } from "@/app/rootReducer";
import { fetchCampaignBillingBatchesAction } from "../slices/campaignBillingBatchesSlice";
import { FetchCampaignBillingBatchesRequest } from "@/api/fetchCampaignBillingBatches/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { SortingState } from "@tanstack/react-table";
import { getISOWeekInfo } from "@/utils/dateUtils";

interface CampaignBillingFilters {
  weeks?: number[];
  year?: number;
  batchStatusId?: number;
  searchTerm?: string;
}

export function useCampaignBillingBatches() {
  const dispatch = useDispatch<AppDispatch>();
  const { batches, totalCount, totalActualQuantity, loading, error } = useSelector(
    (state: RootState) => state.campaignBillingBatches,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  // Initialize with current week and year
  const { week: currentWeek, year: currentYear } = getISOWeekInfo(new Date());
  const [filters, setFilters] = useState<CampaignBillingFilters>({
    weeks: [currentWeek],
    year: currentYear,
  });

  useEffect(() => {
    const requestData: FetchCampaignBillingBatchesRequest = {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      weeks:
        filters.weeks && filters.weeks.length > 0 ? filters.weeks : undefined,
      year: filters.year,
      batchStatusId: filters.batchStatusId,
      searchTerm: filters.searchTerm,
      billingType: "metered",
      sortBy: sorting.length > 0 ? sorting[0].id : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : undefined,
    };
    dispatch(fetchCampaignBillingBatchesAction(requestData));
  }, [dispatch, pagination, sorting, filters]);

  const handlePaginationChange = useCallback(
    (newPagination: PaginationState) => {
      setPagination(newPagination);
    },
    [],
  );

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: Partial<CampaignBillingFilters>) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
      // Reset to first page when filters change
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [],
  );

  const refetch = useCallback(() => {
    const requestData: FetchCampaignBillingBatchesRequest = {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      weeks:
        filters.weeks && filters.weeks.length > 0 ? filters.weeks : undefined,
      year: filters.year,
      batchStatusId: filters.batchStatusId,
      searchTerm: filters.searchTerm,
      billingType: "metered",
      sortBy: sorting.length > 0 ? sorting[0].id : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : undefined,
    };
    dispatch(fetchCampaignBillingBatchesAction(requestData));
  }, [dispatch, pagination, sorting, filters]);

  return {
    batches,
    totalCount,
    totalActualQuantity,
    loading,
    error,
    pagination,
    sorting,
    filters,
    setPagination,
    setSorting,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    refetch,
  };
}
